import React, { useState, useEffect, useCallback } from 'react';
import {
    ArrowTopRightOnSquareIcon,
    BuildingStorefrontIcon,
    CheckIcon,
    GlobeAltIcon,
    LinkIcon,
    MapPinIcon,
    PencilSquareIcon,
    WrenchScrewdriverIcon,
    InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import LayoutWrapper from '../components/LayoutWrapper';
import Helper from '../helpers';
import config from '../config';

const baseUrl = config.apiBaseUrl;

// Base URLs for each social platform — stored value is username only
const SOCIAL_BASES = {
    tikTokUrl:    'tiktok.com/@',
    blueskyUrl:   'bsky.app/profile/',
    instagramUrl: 'instagram.com/',
    facebookUrl:  'facebook.com/',
    stravaUrl:    'strava.com/athletes/',
    linkedInUrl:  'linkedin.com/in/',
};

/** Strip the base URL leaving only the username/handle */
const stripBase = (key, value) => {
    if (!value) return '';
    const base = SOCIAL_BASES[key];
    if (!base) return value;
    // Remove any leading protocol + optional www
    let v = value.replace(/^https?:\/\/(www\.)?/, '').replace(/^www\./, '');
    // Remove the base prefix if present
    if (v.toLowerCase().startsWith(base.toLowerCase())) v = v.slice(base.length);
    // Remove leading @ if the platform already includes it in the base
    if (!base.endsWith('@') && v.startsWith('@')) v = v.slice(1);
    return v.replace(/\/$/, ''); // strip trailing slash
};

const SERVICE_OPTIONS = [
    { key: 'diet_meal_plans',       label: 'Diet / Meal Plans' },
    { key: 'sports_massage',        label: 'Sports Massage' },
    { key: 'consultations',         label: 'Consultations' },
    { key: 'online_coaching',       label: 'Online Coaching' },
    { key: 'group_sessions',        label: 'Group Sessions' },
    { key: 'nutrition_advice',      label: 'Nutrition Advice' },
    { key: 'body_composition',      label: 'Body Composition Testing' },
    { key: 'rehabilitation',        label: 'Rehabilitation / Injury Support' },
    { key: 'pre_post_natal',        label: 'Pre / Post Natal' },
    { key: 'mindset_coaching',      label: 'Mindset & Wellbeing Coaching' },
    { key: 'strength_conditioning', label: 'Strength & Conditioning' },
    { key: 'youth_training',        label: 'Youth / Junior Training' },
    { key: 'gym_facilities',        label: 'Gym Facilities' },
    { key: 'classes',               label: 'Classes' },
    { key: 'personal_training',     label: 'Personal Training' },
    { key: 'swimming',              label: 'Swimming' },
    { key: 'spa_wellness',          label: 'Spa & Wellness' },
];

const VenueDirectoryListing = () => {
    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState('');
    const [saveError, setSaveError] = useState('');
    const [platformReferralUsername, setPlatformReferralUsername] = useState('mrchadwick');
    // Read-only address preview — managed via Venue Settings
    const [venueAddress, setVenueAddress] = useState({ line1: '', line2: '', city: '', postcode: '' });
    const [editForm, setEditForm] = useState({
        name: '',
        email: '',
        bio: '',
        priceType: 'from',
        priceFrom: '',
        priceTo: '',
        beaconsUsername: '',
        tikTokUrl: '',
        blueskyUrl: '',
        instagramUrl: '',
        facebookUrl: '',
        stravaUrl: '',
        linkedInUrl: '',
    });
    const [selectedServices, setSelectedServices] = useState([]);

    const token = () => Helper.getCookie('wh-jwt');

    const fetchListing = useCallback(async () => {
        try {
            const res = await fetch(`${baseUrl}/api/ptdirectory/my-venue-listing`, {
                headers: { Authorization: `Bearer ${await token()}` },
            });
            if (res.ok) {
                const data = await res.json();
                if (data.listing) {
                    setListing(data.listing);
                    setVenueAddress({
                        line1:    data.listing.line1    || '',
                        line2:    data.listing.line2    || '',
                        city:     data.listing.city     || '',
                        postcode: data.listing.postcode || '',
                    });
                    setEditForm({
                        name:            data.listing.name || '',
                        email:           data.listing.email || '',
                        bio:             data.listing.bio || '',
                        priceType:       data.listing.priceType || 'from',
                        priceFrom:       data.listing.priceFrom ?? '',
                        priceTo:         data.listing.priceTo ?? '',
                        beaconsUsername: data.listing.beaconsUsername || '',
                        tikTokUrl:       stripBase('tikTokUrl',    data.listing.tikTokUrl),
                        blueskyUrl:      stripBase('blueskyUrl',   data.listing.blueskyUrl),
                        instagramUrl:    stripBase('instagramUrl', data.listing.instagramUrl),
                        facebookUrl:     stripBase('facebookUrl',  data.listing.facebookUrl),
                        stravaUrl:       stripBase('stravaUrl',    data.listing.stravaUrl),
                        linkedInUrl:     stripBase('linkedInUrl',  data.listing.linkedInUrl),
                    });
                    setSelectedServices(
                        data.listing.services ? data.listing.services.split(',').filter(Boolean) : []
                    );
                }
            }
        } catch (err) {
            console.error('Error fetching venue listing:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchListing();
        // Fetch the platform referral username for the Beacons sign-up link
        fetch(`${baseUrl}/api/platform-settings/beacons-referral-username`)
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d?.username) setPlatformReferralUsername(d.username); })
            .catch(() => {});
    }, [fetchListing]);

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setSaveMsg('');
        setSaveError('');

        try {
            const body = {
                name:            editForm.name.trim() || null,
                email:           editForm.email.trim() || null,
                bio:             editForm.bio.trim() || null,
                priceType:       editForm.priceType || null,
                priceFrom:       editForm.priceFrom !== '' ? parseFloat(editForm.priceFrom) : null,
                priceTo:         editForm.priceTo !== '' ? parseFloat(editForm.priceTo) : null,
                services:        selectedServices.length > 0 ? selectedServices.join(',') : null,
                beaconsUsername: editForm.beaconsUsername.trim() || null,
                // Store usernames only — base URL added at display time
                tikTokUrl:       stripBase('tikTokUrl',    editForm.tikTokUrl),
                blueskyUrl:      stripBase('blueskyUrl',   editForm.blueskyUrl),
                instagramUrl:    stripBase('instagramUrl', editForm.instagramUrl),
                facebookUrl:     stripBase('facebookUrl',  editForm.facebookUrl),
                stravaUrl:       stripBase('stravaUrl',    editForm.stravaUrl),
                linkedInUrl:     stripBase('linkedInUrl',  editForm.linkedInUrl),
            };

            const res = await fetch(`${baseUrl}/api/ptdirectory/my-venue-listing`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${await token()}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            });

            const data = await res.json();
            if (res.ok && data.status === 'success') {
                setSaveMsg('Directory listing saved successfully.');
                setListing(data.listing);
            } else {
                setSaveError(data.message || 'Failed to save listing.');
            }
        } catch (err) {
            setSaveError('An unexpected error occurred.');
        } finally {
            setSaving(false);
        }
    };

    const toggleService = (key) => {
        setSelectedServices(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    const fieldClass = 'w-full px-3 py-2 bg-gray-700 border border-dark-600 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
    const labelClass = 'block text-sm font-medium text-gray-300 mb-1';

    if (loading) {
        return (
            <LayoutWrapper title="Venue Directory Listing">
                <div className="text-gray-400 text-sm">Loading your venue listing…</div>
            </LayoutWrapper>
        );
    }

    return (
        <LayoutWrapper
            title="Venue Directory Listing"
            subtitle="Manage your venue's public profile on the WorldsHighStreet directory."
        >
            <form onSubmit={handleSave} className="space-y-6 max-w-2xl">

                {/* Basic Info */}
                <div className="bg-dark-900 border border-dark-700 rounded-xl p-6">
                    <h2 className="flex items-center gap-2 text-white font-semibold mb-4">
                        <BuildingStorefrontIcon className="h-5 w-5 text-primary-400" />
                        Venue Details
                    </h2>
                    <div className="space-y-4">
                        <div>
                            <label className={labelClass}>Venue Name</label>
                            <input className={fieldClass} value={editForm.name}
                                onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                                placeholder="Your venue name" />
                        </div>
                        <div>
                            <label className={labelClass}>Contact Email</label>
                            <input className={fieldClass} value={editForm.email}
                                   onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))}
                                   placeholder="Directory contact email" />
                        </div>
                        <div>
                            <label className={labelClass}>Description / Bio</label>
                            <textarea className={fieldClass} rows={4} value={editForm.bio}
                                onChange={e => setEditForm(p => ({ ...p, bio: e.target.value }))}
                                placeholder="Describe your venue, what makes it unique, facilities, etc." />
                        </div>
                    </div>
                </div>

                {/* Address preview — read-only, managed via Venue Settings */}
                <div className="bg-dark-900 border border-dark-700 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="flex items-center gap-2 text-white font-semibold">
                            <MapPinIcon className="h-5 w-5 text-primary-400" />
                            Address
                        </h2>
                        <Link
                            to="/dashboard/venue-settings"
                            className="flex items-center gap-1.5 text-xs text-emerald-400 hover:text-emerald-300 transition-colors"
                        >
                            <PencilSquareIcon className="h-4 w-4" />
                            Edit in Venue Settings
                        </Link>
                    </div>

                    {(venueAddress.line1 || venueAddress.city || venueAddress.postcode) ? (
                        <div className="flex items-start gap-3">
                            <MapPinIcon className="h-5 w-5 text-gray-500 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-gray-300 leading-relaxed">
                                {venueAddress.line1 && <div>{venueAddress.line1}</div>}
                                {venueAddress.line2 && <div>{venueAddress.line2}</div>}
                                {venueAddress.city  && <div>{venueAddress.city}</div>}
                                {venueAddress.postcode && <div className="uppercase">{venueAddress.postcode}</div>}
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-start gap-3 p-3 bg-amber-900/20 border border-amber-700/40 rounded-lg">
                            <InformationCircleIcon className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm text-amber-300 font-medium">No address set</p>
                                <p className="text-xs text-gray-400 mt-0.5">
                                    Add your address in{' '}
                                    <Link to="/venue-settings" className="text-emerald-400 hover:underline">
                                        Venue Settings
                                    </Link>
                                    {' '}to show an accurate map pin on your directory profile.
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Pricing */}
                <div className="bg-dark-900 border border-dark-700 rounded-xl p-6">
                    <h2 className="flex items-center gap-2 text-white font-semibold mb-4">
                        <MapPinIcon className="h-5 w-5 text-primary-400" />
                        Pricing
                    </h2>
                    <div className="flex gap-4 mb-4">
                        {['from', 'range'].map(type => (
                            <label key={type} className="flex items-center gap-2 cursor-pointer">
                                <input type="radio" name="priceType" value={type}
                                    checked={editForm.priceType === type}
                                    onChange={() => setEditForm(p => ({ ...p, priceType: type }))}
                                    className="accent-primary-500" />
                                <span className="text-gray-300 text-sm capitalize">{type === 'from' ? 'From price' : 'Price range'}</span>
                            </label>
                        ))}
                    </div>
                    <div className="flex gap-4">
                        <div className="flex-1">
                            <label className={labelClass}>{editForm.priceType === 'range' ? 'From (£)' : 'Price from (£)'}</label>
                            <input type="number" min="0" step="0.01" className={fieldClass}
                                value={editForm.priceFrom}
                                onChange={e => setEditForm(p => ({ ...p, priceFrom: e.target.value }))}
                                placeholder="e.g. 25" />
                        </div>
                        {editForm.priceType === 'range' && (
                            <div className="flex-1">
                                <label className={labelClass}>To (£)</label>
                                <input type="number" min="0" step="0.01" className={fieldClass}
                                    value={editForm.priceTo}
                                    onChange={e => setEditForm(p => ({ ...p, priceTo: e.target.value }))}
                                    placeholder="e.g. 60" />
                            </div>
                        )}
                    </div>
                </div>

                {/* Services */}
                <div className="bg-dark-900 border border-dark-700 rounded-xl p-6">
                    <h2 className="flex items-center gap-2 text-white font-semibold mb-4">
                        <WrenchScrewdriverIcon className="h-5 w-5 text-primary-400" />
                        Services Offered
                    </h2>
                    <div className="flex flex-wrap gap-2">
                        {SERVICE_OPTIONS.map(({ key, label }) => {
                            const active = selectedServices.includes(key);
                            return (
                                <button key={key} type="button"
                                    onClick={() => toggleService(key)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${
                                        active
                                            ? 'bg-primary-600 border-primary-500 text-white'
                                            : 'bg-dark-800 border-dark-600 text-gray-400 hover:border-primary-500 hover:text-white'
                                    }`}
                                >
                                    {active && <CheckIcon className="h-3.5 w-3.5" />}
                                    {label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Social Links */}
                <div className="bg-dark-900 border border-dark-700 rounded-xl p-6">
                    <h2 className="flex items-center gap-2 text-white font-semibold mb-4">
                        <GlobeAltIcon className="h-5 w-5 text-primary-400" />
                        Social Media &amp; Links
                    </h2>

                    {/* Beacons.ai — referral sign-up or username entry */}
                    <div className="mb-5 pb-5 border-b border-dark-700">
                        <div className="flex items-center gap-2 mb-2">
                            <LinkIcon className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                            <span className="text-sm font-medium text-gray-300">Beacons.ai</span>
                        </div>

                        {!editForm.beaconsUsername ? (
                            /* No username yet — prompt to sign up via referral */
                            <div className="mb-3 p-3 rounded-lg bg-emerald-900/20 border border-emerald-700/40">
                                <p className="text-sm text-emerald-300 font-medium mb-1">
                                    Don't have a Beacons.ai account yet?
                                </p>
                                <p className="text-xs text-gray-400 mb-3">
                                    Another string to boost the power of your social bow. Beacons.ai is a free promotional page.
                                </p>
                                <a
                                    href={`https://beacons.ai/signup?c=${platformReferralUsername}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors"
                                >
                                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                    Create your free Beacons.ai account
                                </a>
                                <p className="text-xs text-gray-500 mt-2">
                                    Once you've signed up, enter your username below and it will appear on
                                    your directory profile.
                                </p>
                            </div>
                        ) : (
                            /* Username is set — show live link */
                            <p className="text-xs text-gray-400 mb-2">
                                Your Beacons profile:{' '}
                                <a
                                    href={`https://beacons.ai/${editForm.beaconsUsername}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-emerald-400 hover:underline"
                                >
                                    beacons.ai/{editForm.beaconsUsername}
                                </a>
                            </p>
                        )}

                        {/* Username input — always visible */}
                        <div className="flex items-center gap-2 mt-1">
                            <span className="text-gray-500 text-sm flex-shrink-0">beacons.ai/</span>
                            <input
                                type="text"
                                value={editForm.beaconsUsername}
                                onChange={e => setEditForm(p => ({
                                    ...p,
                                    beaconsUsername: e.target.value.replace(/^@/, '')
                                }))}
                                className={`${fieldClass} flex-1`}
                                placeholder="your-username"
                            />
                        </div>
                    </div>

                    {/* Other social links */}
                    <div className="space-y-3">
                        {[
                            { key: 'tikTokUrl',    label: 'TikTok',    placeholder: 'yourvenue' },
                            { key: 'blueskyUrl',   label: 'Bluesky',   placeholder: 'yourvenue.bsky.social' },
                            { key: 'instagramUrl', label: 'Instagram', placeholder: 'yourvenue' },
                            { key: 'facebookUrl',  label: 'Facebook',  placeholder: 'yourvenuepage' },
                            { key: 'stravaUrl',    label: 'Strava',    placeholder: '12345678' },
                            { key: 'linkedInUrl',  label: 'LinkedIn',  placeholder: 'yourvenue' },
                        ].map(({ key, label, placeholder }) => (
                            <div key={key} className="flex items-center gap-0">
                                <span className="text-gray-500 text-xs bg-gray-800 border border-r-0 border-dark-600 rounded-l-lg px-2 py-2.5 whitespace-nowrap min-w-[10rem]">
                                    {SOCIAL_BASES[key]}
                                </span>
                                <input
                                    type="text"
                                    value={editForm[key]}
                                    onChange={e => setEditForm(p => ({ ...p, [key]: stripBase(key, e.target.value) }))}
                                    className={`${fieldClass} rounded-l-none flex-1`}
                                    placeholder={placeholder}
                                    aria-label={label}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Edit links */}
                <div className="bg-dark-900 border border-dark-700 rounded-xl p-6">
                    <h2 className="flex items-center gap-2 text-white font-semibold mb-4">
                        <PencilSquareIcon className="h-5 w-5 text-primary-400" />
                        Directory Profile
                    </h2>
                    {listing?.slug ? (
                        <p className="text-sm text-gray-400">
                            Your public profile:{' '}
                            <a
                                href={`/pt/${listing.slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary-400 hover:underline"
                            >
                                /pt/{listing.slug}
                            </a>
                        </p>
                    ) : (
                        <p className="text-sm text-gray-500">Save your details above to generate your public profile URL.</p>
                    )}
                </div>

                {/* Save button */}
                <div>
                    <button
                        type="submit"
                        disabled={saving}
                        className="px-6 py-2.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg font-medium transition-colors"
                    >
                        {saving ? 'Saving…' : 'Save Listing'}
                    </button>
                    {saveMsg && <p className="mt-2 text-sm text-emerald-400">✓ {saveMsg}</p>}
                    {saveError && <p className="mt-2 text-sm text-red-400">{saveError}</p>}
                </div>
            </form>
        </LayoutWrapper>
    );
};

export default VenueDirectoryListing;
